import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { openCashfree } from "@/pages/Checkout";
import { PhoneLogin } from "@/components/auth/PhoneLogin";
import { firebaseEnabled } from "@/lib/firebase";

const Shell = ({ children }) => (
  <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 text-center" data-testid="app-pay-page">{children}</div>
);

export function AppPayDone() {
  const [sp] = useSearchParams();
  return (
    <Shell>
      <CheckCircle2 className="h-12 w-12 text-emerald-600" />
      <h1 className="mt-4 font-display text-2xl font-semibold">Payment step complete</h1>
      <p className="mt-2 max-w-sm text-sm text-neutral-600">{sp.get("change_id") ? `Go back to the Hoorecars app — your booking ${sp.get("booking_id")} will show the new dates once the payment is confirmed.` : `Go back to the Hoorecars app to see your booking ${sp.get("booking_id")}. Your confirmation and GST invoice are also sent by email.`}</p>
      <a href="hoorecars://" data-testid="app-pay-return" className="mt-6 rounded-full bg-[#FF5A00] px-6 py-3 text-sm font-semibold text-white">Return to the app</a>
    </Shell>
  );
}

export function EmailVerified() {
  const [sp] = useSearchParams();
  const ok = sp.get("ok") === "1";
  return (
    <Shell>
      {ok ? <CheckCircle2 className="h-12 w-12 text-emerald-600" /> : <XCircle className="h-12 w-12 text-rose-500" />}
      <h1 className="mt-4 font-display text-2xl font-semibold" data-testid="email-verified-title">{ok ? "Email verified" : "This link has expired"}</h1>
      <p className="mt-2 max-w-sm text-sm text-neutral-600">{ok ? "Thanks, your email is confirmed. You can close this page and carry on in the Hoorecars app or website." : "The link is old or already used. You can keep using Hoorecars; sign in and we'll send a new link if needed."}</p>
      <a href="/" data-testid="email-verified-home" className="btn-primary mt-6">Go to Hoorecars</a>
    </Shell>
  );
}

export function AppPhone() {
  const [done, setDone] = useState("");
  const back = (value, isNew) => {
    // Short one-time code (no dots) — the app swaps it for a fresh Clerk ticket. Long tickets only if the server sent one.
    const param = value.includes(".") ? `ticket=${encodeURIComponent(value)}` : `code=${value}`;
    const url = `hoorecars://phone-auth?${param}&new=${isNew ? 1 : 0}`;
    setDone(url);
    window.location.href = url;
  };
  return (
    <Shell>
      {!firebaseEnabled ? <p className="text-sm text-rose-600" data-testid="app-phone-disabled">Mobile sign-in is not available right now. Please try again in a few minutes.</p>
        : done ? (
          <>
            <CheckCircle2 className="h-12 w-12 text-hc-success" />
            <h1 className="mt-4 font-display text-2xl font-semibold">You're verified</h1>
            <a href={done} data-testid="app-phone-return" className="btn-primary mt-6">Return to the app</a>
          </>
        ) : <PhoneLogin onTicket={back} app footer="Your number is verified securely by Google Firebase." />}
    </Shell>
  );
}

export default function AppPay() {
  const [sp] = useSearchParams();
  const [err, setErr] = useState("");
  useEffect(() => {
    const sid = sp.get("sid");
    if (!sid) { setErr("This payment link is incomplete. Please start again from the app."); return; }
    openCashfree(sid, sp.get("mode")).catch(() => setErr("Couldn't open the payment page. Please go back to the app and try again."));
  }, [sp]);
  return (
    <Shell>
      {err ? <p className="text-sm text-rose-600" data-testid="app-pay-error">{err}</p> : <><Loader2 className="h-8 w-8 animate-spin text-[#FF5A00]" /><p className="mt-4 text-sm text-neutral-600">Opening secure payment…</p></>}
    </Shell>
  );
}
